/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import {AppContext} from "../../misc/appContext";
import ApiKey from "../../../models/apiKeyModel";
import {onSetCrumbs} from "../../../util/events";
import {ResponseData, showDialog, showSnackbar, useEvent} from '@dvrd/dvr-controls';
import {createErrorMessage} from "../../../util/utils";
import ApiSettings from "./apiSettings";
import {ApiKeyStatus} from "../../../util/interfaces";
import {onConnectBolOauth} from "../../../util/bolOauthUtil";
import {saveToLocal} from "../../../util/storageUtil";
import {SELECTED_ACCOUNT_KEY} from "../../../util/constants";

export default function ApiSettingsController() {
    const context = useContext(AppContext);
    const [apiKeys, setApiKeys] = useState<Array<ApiKey>>([]);
    const [loading, setLoading] = useState(true);

    function onRenew() {
        const {account} = context.accountContext;
        if (!account) return;
        const {user} = context.userContext;
        if (user?.isDemoAccount)
            return showDialog('Deze actie is niet toegestaan in het demo account.', 'Actie niet toegestaan');
        saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
        onConnectBolOauth();
    }

    const onClickActivate = (apiKey: ApiKey) => () => {
        const {account} = context.accountContext;
        if (!account) return;
        if(apiKey.useOAuth) {
            saveToLocal(SELECTED_ACCOUNT_KEY, account.id);
            return onConnectBolOauth();
        }
        setLoading(true);
        apiKey.activate((apiKey: ApiKey, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                if (apiKey.status === ApiKeyStatus.ERROR) {
                    showDialog('De API koppeling is verlopen en kan niet meer gebruikt worden. Je kunt in je Bol ' +
                        'account nieuwe API sleutels aanmaken en de API koppeling daarmee instellen via de Wijzigen ' +
                        'optie van de API koppeling.', 'Koppeling verlopen');
                } else showSnackbar('De API koppeling is geactiveerd');
                loadKeys();
            } else showDialog(data.message ?? createErrorMessage('Het activeren van de API key is niet gelukt.'), 'Activeren mislukt');
        })
    };

    function onDeactivateKey(apiKey: ApiKey) {
        return function () {
            showDialog('Weet je zeker dat je deze API koppeling wilt verbreken?', 'API koppeling verbreken', ['Nee',
                {label: 'Ja', onClick: onConfirmDeactivate(apiKey), primary: false}]);
        }
    }

    function onConfirmDeactivate(apiKey: ApiKey) {
        return function () {
            const {account} = context.accountContext;
            if (!account) return;
            setLoading(true);
            apiKey.deactivate((apiKey: ApiKey, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showSnackbar('De API koppeling is verbroken');
                    account.replaceApiKey(apiKey);
                    context.onChangeContext({account});
                    loadKeys();
                } else
                    showDialog(createErrorMessage(data.message ??
                        'Het deactiveren van de API koppeling is niet gelukt.'), 'Deactivatie mislukt');
            })
        }
    }

    function loadKeys() {
        const {account} = context.accountContext;
        if (account) {
            if (!loading) setLoading(true);
            ApiKey.getAll(account.id, (apiKeys: ApiKey[], success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setApiKeys(apiKeys);
                } else showDialog(data.message ?? createErrorMessage('Het laden van je API keys is niet gelukt.'), 'Laden mislukt');
            });
        }
    }

    useEffect(() => {
        loadKeys();
    }, [context.accountContext.account]);

    useEffect(() => {
        onSetCrumbs([{label: 'Instellingen'}, {label: 'API'}]);
    }, []);

    return (
        <ApiSettings onClickActivate={onClickActivate} apiKeys={apiKeys} loading={loading} onClickAdd={onRenew}
                     onRenew={onRenew} onDeactivateKey={onDeactivateKey}/>
    );
}